import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type SearchableSelectOption = { value: string; label: string; rowKey?: string };

type SearchableSelectProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  disabled?: boolean;
  /** Shown when the current value has no matching option label. */
  placeholder?: string;
  className?: string;
  /** Match `.geo-zones-add-row` / dashboard field styling. */
  variant?: "form" | "dash";
};

export function SearchableSelect({
  id,
  value,
  onChange,
  options,
  disabled = false,
  placeholder = "Select…",
  className = "",
  variant = "form",
}: SearchableSelectProps) {
  const listId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelBox, setPanelBox] = useState<{
    top: number;
    left: number;
    width: number;
    listMaxHeight: number;
  } | null>(null);

  const selectedLabel = useMemo(() => {
    const hit = options.find((o) => o.value === value);
    if (hit) return hit.label;
    if (value === "" && placeholder) return placeholder;
    return value || placeholder;
  }, [options, value, placeholder]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, filter]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(filtered.length > 0 ? 0 : -1);
  }, [filter, open, filtered.length]);

  const updatePanelPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const margin = 8;
    const searchApprox = 42;
    let width = rect.width;
    let left = rect.left;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - width - margin);
    }
    const spaceBelow = window.innerHeight - rect.bottom - gap - searchApprox - margin;
    const listMaxHeight = Math.max(100, Math.min(240, spaceBelow, window.innerHeight * 0.38));
    setPanelBox({
      top: rect.bottom + gap,
      left,
      width,
      listMaxHeight,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelBox(null);
      return;
    }
    updatePanelPosition();
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    const ro = new ResizeObserver(() => updatePanelPosition());
    if (triggerRef.current) ro.observe(triggerRef.current);
    window.addEventListener("scroll", updatePanelPosition, true);
    window.addEventListener("resize", updatePanelPosition);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", updatePanelPosition, true);
      window.removeEventListener("resize", updatePanelPosition);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (open && panelBox && searchRef.current) {
      searchRef.current.focus();
      searchRef.current.select();
    }
  }, [open, panelBox]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
      setFilter("");
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    setFilter("");
  }, []);

  const pick = useCallback(
    (v: string) => {
      onChange(v);
      close();
    },
    [onChange, close],
  );

  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  }

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && filtered[activeIndex]) {
        pick(filtered[activeIndex].value);
      }
    }
  }

  const variantClass = variant === "dash" ? " searchable-select--dash" : "";

  const panel =
    open && panelBox
      ? createPortal(
          <div
            ref={panelRef}
            className={`searchable-select__panel searchable-select__panel--portal${variantClass}`}
            role="presentation"
            style={{
              position: "fixed",
              top: panelBox.top,
              left: panelBox.left,
              width: panelBox.width,
              zIndex: 10050,
            }}
          >
            <input
              ref={searchRef}
              type="search"
              className="searchable-select__search"
              placeholder="Type to filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={onSearchKeyDown}
              aria-autocomplete="list"
              aria-controls={listId}
              autoComplete="off"
            />
            <ul
              id={listId}
              className="searchable-select__list"
              role="listbox"
              style={{ maxHeight: panelBox.listMaxHeight }}
            >
              {filtered.length === 0 ? (
                <li className="searchable-select__empty" role="presentation">
                  No matches
                </li>
              ) : (
                filtered.map((o, idx) => (
                  <li key={o.rowKey ?? (o.value === "" ? "__empty__" : o.value || `opt-${idx}`)} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={o.value === value}
                      className={
                        "searchable-select__option" +
                        (idx === activeIndex ? " searchable-select__option--active" : "")
                      }
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => pick(o.value)}
                    >
                      {o.label}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={`searchable-select${variantClass} ${className}`.trim()} data-open={open ? "true" : undefined}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className="searchable-select__trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="searchable-select__trigger-text">{selectedLabel}</span>
        <span className="searchable-select__chevron" aria-hidden>
          ▾
        </span>
      </button>
      {panel}
    </div>
  );
}
